<script setup lang="ts">
import {reactive, watch} from "vue";
import {useRouter} from "vue-router";
import {Button, ConfirmDialog} from "@/Components/Library";
import Notifications from "./Notifications.vue";
import InstallPrompt from "./InstallPrompt.vue";
import MenuDrawer from "./MenuDrawer.vue";
import {useMainStore} from "@/Stores/store";
import {useStorage, useWindowScroll} from "@vueuse/core";
import OnboardingDialog from "../Pages/Auth/OnboardingDialog.vue";
import UserSidebar from "@/Layouts/UserSidebar.vue";
import useEcho from "@/Composables/echo";
import {useRoute} from "vue-router";
import UserLayoutEvents from "./user-layout-events.ts";
import {useInstall} from "@/Composables/useInstall";
import AskAlbieDrawer from "@/Components/Global/AskAlbieDrawer.vue";

const $store = useMainStore();
const router = useRouter();
const route = useRoute();
const showOnboarding = useStorage("onboarding", true);
const {x, y} = useWindowScroll();
const {isInstallAvailable, showInstallPrompt} = useInstall();

const bottomMenu = [
    {
        label: "Feed",
        icon: "material-symbols:feed",
        command: () => {
            router.push({name: "feed"});
        },
    },
    {
        label: "Contacts",
        icon: "ph:address-book-fill",
        command: () => {
            router.push({name: "contacts.index"});
        },
    },
    {
        label: "Groups",
        icon: "fluent:people-group-28-filled",
        command: () => {
            router.push({name: "groups.index"});
        },
    },
    {
        label: "Stimuli",
        icon: "bxs:image",
        command: () => {
            router.push({name: "stimuli.index"});
        },
    },
    // {
    //     label: "Profile",
    //     icon: "fluent:person-28-filled",
    //     command: () => {
    //         router.push({name: "profile.edit"});
    //     },
    // },
    // {
    //     label: "Add",
    //     icon: "mdi:plus",
    //     command: () => {
    //         router.push({name: "groups.index"});
    //     },
    // },
];

const state = reactive({
    show: {
        drawer: false,
        // helpDialog: false,
        askAlbieDrawer: false,
        header: true,
        sidebar: true,
    },
    lastScrollY: 0,
});

useEcho(UserLayoutEvents($store, route));

watch(y, (nVal) => {
    // Determine scroll direction and update header visibility
    if (nVal > state.lastScrollY && nVal > 50) {
        // Scrolling down and not at the top
        state.show.header = false;
    } else if (nVal < state.lastScrollY) {
        // Scrolling up
        state.show.header = true;
    }

    // Update last scroll position
    state.lastScrollY = nVal;
});
</script>
<template>
    <div class="max-w-screen flex w-full grow flex-col dark:bg-transparent">
        <div class="max-w-screen flex w-full grow flex-col">
            <UserSidebar @show-ask-albie-drawer="state.show.askAlbieDrawer = true" v-model="state.show.sidebar" />
            <div class="pb--8 flex w-full grow flex-col transition-all" :class="{'xl:pl-64': state.show.sidebar}">
                <!-- header -->
                <div
                    class="m-container h--14 xl:pb--0 pointer-events-none sticky top-0 z-50 mx-auto flex w-full bg-stone-100 py-3 text-askr-green-500 transition duration-300 xl:relative xl:w-full xl:max-w-full xl:bg-transparent xl:px-4 dark:bg-stone-950 dark:xl:bg-transparent"
                    :class="{
                        'translate-y-0': state.show.header,
                        '-translate-y-full xl:translate-y-0': !state.show.header,
                    }"
                >
                    <div class="pointer-events-auto">
                        <a href="/">
                            <h1 class="text-3xl font-black tracking-[-0.175rem] xl:hidden">ASKr</h1>
                        </a>
                        <Button
                            class="ml-auto hidden xl:block"
                            :icon="{icon: 'iconamoon:menu-burger-horizontal-bold', width: '26'}"
                            @click="state.show.sidebar = !state.show.sidebar"
                        />
                    </div>
                    <div class="pointer-events-auto ml-auto flex items-center">
                        <Button
                            v-if="isInstallAvailable"
                            @click="showInstallPrompt"
                            icon="ic:round-install-mobile"
                            text
                            rounded
                            class="mr-3 text-askr-green-500 hover:bg-askr-green-500/10"
                            title="Install app"
                        />
                        <Button
                            class="mr-3"
                            :icon="{icon: 'ri:robot-2-fill', width: '24'}"
                            @click="state.show.askAlbieDrawer = true"
                        />
                        <Notifications />
                        <Button
                            class="ml-3 block xl:hidden"
                            text
                            rounded
                            aria-label="User"
                            @click="state.show.drawer = true"
                            :icon="{icon: 'iconamoon:menu-burger-horizontal-bold', width: '26'}"
                        />
                    </div>
                </div>
                <!-- content -->
                <slot name="container">
                    <div class="xl:px--4 relative mx-auto flex w-full grow flex-col">
                        <slot />
                    </div>
                </slot>
            </div>
        </div>
        <!--        <div class="md:shadow&#45;&#45;xl sticky bottom-0 z-50 w-screen max-w-3xl md:bottom-3 md:px-3 xl:px-0">-->
        <!--            <div class="flex items-center bg-askr-green md:rounded-full md:shadow-stone-600">-->
        <!--                <template v-for="(item, i) in bottomMenu">-->
        <!--                    <Button-->
        <!--                        :icon="{icon: item.icon, height: 24}"-->
        <!--                        @click="item.command"-->
        <!--                        rounded-->
        <!--                        class="grow flex-col !gap-0 rounded-none !px-1 !py-2 text-xs !font-normal text-white hover:bg-askr-green-600/20"-->
        <!--                        :class="[{'rounded-l-full': i === 0}, {'rounded-r-full': i === bottomMenu.length - 1}]"-->
        <!--                    >-->
        <!--                        {{ item.label }}-->
        <!--                    </Button>-->
        <!--                </template>-->
        <!--            </div>-->
        <!--        </div>-->
        <MenuDrawer v-model="state.show.drawer" @show-ask-albie-drawer="state.show.askAlbieDrawer = true" />
        <ConfirmDialog class="max-w-sm"></ConfirmDialog>
        <!--        <ConfirmPopup class="max-w-md" dusk="confirm-popup">-->
        <!--            <template #message="{ message }">-->
        <!--                <div class="p-5">-->
        <!--                    <strong v-if="message.header">{{ message.header }}</strong>-->
        <!--                    <br v-if="message.header"/>-->
        <!--                    {{ message.message }}-->
        <!--                </div>-->
        <!--            </template>-->
        <!--        </ConfirmPopup>-->
        <!--        <HelpDialog v-model:visible="state.show.helpDialog" />-->
        <AskAlbieDrawer v-model:visible="state.show.askAlbieDrawer" />
        <OnboardingDialog v-model:visible="showOnboarding" />
        <InstallPrompt />
    </div>
</template>
